import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { CloudUpload, Dot, MoreHorizontal, Trash } from 'lucide-react';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useGetAllGroups } from '~entities/groups';
import { useGetAllSessions } from '~entities/session/api/get-sessions';
import {
  type StudentSearch,
  useGetAllStudentsWithSearch,
} from '~entities/student';
import { useBulkDeleteStudent } from '~entities/student/api/bulk-delete-student';
import { useGetAllStudentsForCSV } from '~entities/student/api/get-all-students-for-csv';
import { type GetAllStudentsParams } from '~entities/student/api/get-all-students-with-search';
import { hasMedicalData } from '~entities/student/lib/medical';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { FamilyNameFilter } from '~features/students/ui/family-name-filter';
import { StudentLastNameFilter } from '~features/students/ui/student-last-name-filter';

import { useGetCurrency } from '~shared/api/currency-api';
import { StudentDetailsCrmLink } from '~shared/components/student-details-crm-link';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { MedicalBadgeWithTooltip } from '~shared/components/medical-details';
import { DataTableColumnHeader } from '~shared/components/table';
import { DataTableColumnHeaderWithFilter } from '~shared/components/table/header-with-filters';
import {
  mergeSelectionCache,
  resolveSelectedRows,
} from '~shared/components/table/resolve-selected-rows';
import { exportToCSV } from '~shared/helpers/csv';
import {
  calculateAgeFromISO,
  formatDateOnlySafe,
  isValidDateValue,
} from '~shared/helpers/datetime';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import { useFilterPersistence } from '~shared/hooks/useFilterPersistence';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import { DeleteStudentDialog } from './delete-student-dialog';

const STUDENT_SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  familyName: 'familyName',
  gender: 'gender',
  status: 'status',
  dob: 'dob',
  age: 'ageInMonths',
  totalOutstanding: 'totalOutstanding',
  costumeSize: 'tShirtSize',
  createdAt: 'createdAt',
};

const areSortingStatesEqual = (a: SortingState, b: SortingState) => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((item, index) => {
    const other = b[index];

    if (!other) {
      return false;
    }

    return item.id === other.id && item.desc === other.desc;
  });
};

// Create a column helper
const columnHelper = createColumnHelper<StudentSearch>();

// Define columns

export function StudentListTable() {
  const { hasAdminAccess, hasSuperAdminAccess } = useCurrentUserRole();
  const { data: currencyDetails } = useGetCurrency();
  const selectedStudentsByIdRef = useRef<Record<string, StudentSearch>>({});
  const cols = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => {
              table.toggleAllPageRowsSelected(!!value);
            }}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => {
          return (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => {
                row.toggleSelected(!!value);
              }}
              aria-label='Select row'
            />
          );
        },
      }),
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeaderWithFilter
            className='pl-4 text-black'
            info={info}
            title='Student Name'
          />
        ),
        cell: (info) => (
          <div className='flex items-center gap-2'>
            <Button
              variant={'link'}
              className='capitalize text-custom-secondary-1'
            >
              <StudentDetailsCrmLink studentId={info.row.original._id}>
                {info.getValue()}
              </StudentDetailsCrmLink>
            </Button>
            <MedicalBadgeWithTooltip medical={info.row.original.medical} />
          </div>
        ),
      }),
      columnHelper.accessor('familyName', {
        id: 'familyName',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Family Name'
          />
        ),
        cell: (info) => (
          <Button
            variant={'link'}
            className='capitalize text-custom-secondary-1'
            onClick={() => {
              if (!hasAdminAccess) {
                toast.error('Not enough permissions');
              }
            }}
          >
            <Link
              to={`/family`}
              search={{
                parentId: info.row.original.parentId,
              }}
              disabled={!hasAdminAccess}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('gender', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Gender'
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'> {info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('active', {
        id: 'status',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
          />
        ),
        cell: (info) => {
          const isActive = info.getValue();
          return (
            <div
              className={cn('ml-4 flex w-fit items-center rounded-full pr-2', {
                'bg-[#ECFDF3]': isActive,
                'bg-[#F2F4F7]': !isActive,
              })}
            >
              <Dot
                className={cn({
                  'text-[#14BA6D]': isActive,
                  'text-[#6C778B]': !isActive,
                })}
                strokeWidth={4}
              />
              <span
                className={cn('font-medium', {
                  'text-[#037847]': isActive,
                  'text-[#364254]': !isActive,
                })}
              >
                {info.getValue() ? 'Active' : 'Inactive'}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor('dob', {
        id: 'dob',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Date of Birth'
          />
        ),
        cell: (info) => {
          const dob = info.getValue();
          if (!dob) return <div className='px-4'>-</div>;
          return (
            <div className='px-4'>
              {formatDateOnlySafe(dob, 'MM-dd-yyyy', 'Invalid date')}
            </div>
          );
        },
      }),
      columnHelper.accessor('dob', {
        id: 'age',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Age'
          />
        ),
        cell: (info) => {
          const dob = info.getValue();
          if (!dob) return <div className='px-4'>-</div>;

          const { years, months } = calculateAgeFromISO(dob);
          return (
            <div className='px-4'>
              {`${years} ${years > 1 ? 'years' : 'year'} ${months} ${months > 1 ? 'months' : 'month'}`}
            </div>
          );
        },
      }),
      ...(hasAdminAccess
        ? [
            columnHelper.accessor('totalOutstanding', {
              id: 'totalOutstanding',
              header: (info) => (
                <DataTableColumnHeader
                  className='pl-4 text-black'
                  column={info.column}
                  title='Balance'
                />
              ),
              cell: (info) => (
                <div className='px-4'>
                  {currencyDetails?.symbol}
                  {info.getValue()}
                </div>
              ),
            }),
          ]
        : []),
      columnHelper.accessor('tShirtSize', {
        id: 'costumeSize',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Costume Size'
          />
        ),
        cell: (info) => <div className='px-4'> {info.getValue()}</div>,
      }),

      columnHelper.display({
        id: 'actions',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-right text-black'
            column={info.column}
            title='Actions'
          />
        ),
        cell: (info) => (
          <div className='text-right'>
            <DropdownMenu>
              <DropdownMenuTrigger disabled={!hasSuperAdminAccess} asChild>
                <Button variant='ghost' className='size-8 p-0'>
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  className='text-red-600'
                  onClick={() => {
                    setRowSelection({});
                    selectedStudentsByIdRef.current = {};
                    setSelectedStudents([info.row.original]);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash className='mr-2 size-4' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      }),
    ],
    [hasAdminAccess, hasSuperAdminAccess, currencyDetails],
  );

  // Use the filter persistence hook
  const { filters, pagination, updateFilter, updatePagination } =
    useFilterPersistence({
      query: '',
      family: '',
      lastName: '',
      tag: 'All',
      sessionId: 'All',
      groupId: 'All',
    });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const handleSortingChange = React.useCallback<
    React.Dispatch<React.SetStateAction<SortingState>>
  >(
    (updater) => {
      setSorting((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;

        if (!areSortingStatesEqual(next, prev) && pagination.pageIndex !== 0) {
          updatePagination((prevPagination) =>
            prevPagination.pageIndex === 0
              ? prevPagination
              : { ...prevPagination, pageIndex: 0 },
          );
        }

        return next;
      });
    },
    [pagination.pageIndex, updatePagination],
  );
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<
    Array<StudentSearch>
  >([]);
  const bulkDeleteMutation = useBulkDeleteStudent();

  const activeSort = sorting[0];
  const sortByParam =
    activeSort && STUDENT_SORT_FIELD_MAP[activeSort.id]
      ? STUDENT_SORT_FIELD_MAP[activeSort.id]
      : undefined;
  const sortOrderParam =
    sortByParam && activeSort ? (activeSort.desc ? 'desc' : 'asc') : undefined;

  const studentQueryParams: GetAllStudentsParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    name: filters.query,
    familyName: filters.family ? filters.family : undefined,
    lastName: filters.lastName ? filters.lastName : undefined,
    tags: filters.tag !== 'All' ? filters.tag : undefined,
  };

  // Group enrollment filter requires both session and group — the API ignores
  // either param when sent alone (see searchByProperties on the backend).
  const applyGroupEnrollmentFilter =
    filters.sessionId !== 'All' && filters.groupId !== 'All';

  if (applyGroupEnrollmentFilter) {
    studentQueryParams.sessionId = filters.sessionId;
    studentQueryParams.groupId = filters.groupId;
  }

  if (sortByParam) {
    studentQueryParams.sortBy = sortByParam;
    if (sortOrderParam) {
      studentQueryParams.sortOrder = sortOrderParam;
    }
  }

  const { data: studentsData, isFetching } =
    useGetAllStudentsWithSearch(studentQueryParams);

  const { data: sessionsData } = useGetAllSessions({ page: 1, limit: 100 });
  const { data: groupsData } = useGetAllGroups();
  const sessions = sessionsData?.sessions ?? [];
  const groups = groupsData ?? [];
  const isGroupFilterActive = applyGroupEnrollmentFilter;

  const data = studentsData?.data.students ?? [];
  const totalCount = studentsData?.pagination.totalCount ?? 0;

  selectedStudentsByIdRef.current = mergeSelectionCache(
    selectedStudentsByIdRef.current,
    data,
    rowSelection,
  );
  const selectedStudentsFromState = resolveSelectedRows(
    rowSelection,
    data,
    selectedStudentsByIdRef.current,
  );

  useEffect(() => {
    setRowSelection({});
    selectedStudentsByIdRef.current = {};
  }, [
    filters.query,
    filters.family,
    filters.lastName,
    filters.tag,
    filters.sessionId,
    filters.groupId,
  ]);

  useEffect(() => {
    if (
      !isGroupFilterActive ||
      isFetching ||
      !studentsData ||
      data.length > 0
    ) {
      return;
    }

    toast.info(
      'No active enrollments found for the selected session and group.',
      { id: 'student-group-filter-empty' },
    );
  }, [isGroupFilterActive, isFetching, studentsData, data.length]);

  const familyNames = studentsData?.data.familyNames ?? [];
  const lastNames = studentsData?.data.lastNames ?? [];
  const tags = studentsData?.data.tags ?? [];

  const { refetch: getAllStudentsForCSV } = useGetAllStudentsForCSV({
    enabled: false,
  });

  const handleExport = async () => {
    try {
      const { data: allStudentsData } = await getAllStudentsForCSV();
      if (!allStudentsData) {
        console.error('No data available to export.');
        return;
      }

      // Expanded headers to include separate medical information columns
      const headers = [
        'Student Name',
        'Medical Indicator',
        'Family Name',
        'Gender',
        'Date of Birth',
        'T-Shirt Size',
        'Transportation',
        'Doctor',
        'Medications',
        'Allergies',
        'Disabilities',
        'Special Needs',
        'Health Insurance',
        'Enrollments',
        'Parent',
        'Studio',
      ];

      const formattedData = allStudentsData.map((student) => {
        const medical = {
          primaryDoctor: student.medical.primaryDoctor ?? '',
          medications: student.medical.medications ?? '',
          allergies: student.medical.allergies ?? '',
          disabilities: student.medical.disabilities ?? '',
          specialNeeds: student.medical.specialNeeds ?? '',
          healthInsuranceCarrier: student.medical.healthInsuranceCarrier ?? '',
        };

        return {
          'Student Name': `${capitalizeWords(student.firstName)} ${capitalizeWords(student.lastName)}`,
          'Medical Indicator': hasMedicalData(medical) ? 'Yes' : 'No',
          'Family Name': capitalizeWords(student.familyName),
          Gender: capitalizeWords(student.gender),
          'Date of Birth': isValidDateValue(student.dob)
            ? formatDateOnlySafe(student.dob, 'M/d/yyyy', '')
            : '',
          'T-Shirt Size': student.tShirtSize,
          Transportation: student.transportation,
          Doctor: capitalizeWords(medical.primaryDoctor),
          Medications: capitalizeWords(medical.medications),
          Allergies: capitalizeWords(medical.allergies),
          Disabilities: capitalizeWords(medical.disabilities),
          'Special Needs': capitalizeWords(medical.specialNeeds),
          'Health Insurance': capitalizeWords(medical.healthInsuranceCarrier),
          Enrollments: student.enrollments
            ? student.enrollments.join(', ')
            : '',
          Parent: capitalizeWords(student.parent),
          Studio: capitalizeWords(student.studio),
        };
      });

      exportToCSV(
        formattedData,
        headers,
        `students-export-${new Date().toISOString()}.csv`,
      );
    } catch (error) {
      console.error('Error exporting students:', error);
    }
  };

  return (
    <Card>
      <CardContent className='pt-6'>
        {isGroupFilterActive &&
          !isFetching &&
          studentsData &&
          data.length === 0 && (
            <p className='mb-4 text-sm text-muted-foreground'>
              No active enrollments found for the selected session and group.
            </p>
          )}
        <div>
          <EnrollioTable
            ariaLabel='Students'
            totalLabel='Students'
            filterHeader={
              <>
                <EnrollioTableSearch
                  placeholder='Search students...'
                  value={filters.query}
                  onChange={(value) => {
                    updateFilter('query', value);
                  }}
                />
                <FamilyNameFilter
                  familyNames={familyNames}
                  value={filters.family}
                  onValueChange={(newValue) => {
                    // Handle both string and function update forms of setState
                    const actualValue =
                      typeof newValue === 'function'
                        ? newValue(filters.family)
                        : newValue;

                    updateFilter('family', actualValue);
                  }}
                />
                <StudentLastNameFilter
                  lastNames={lastNames}
                  value={filters.lastName}
                  onValueChange={(newValue) => {
                    const actualValue =
                      typeof newValue === 'function'
                        ? newValue(filters.lastName)
                        : newValue;

                    updateFilter('lastName', actualValue);
                  }}
                />
                {/* Filter Tags */}
                <Select
                  value={filters.tag}
                  onValueChange={(value) => {
                    updateFilter('tag', value);
                  }}
                >
                  <SelectTrigger className='w-full md:w-[160px]'>
                    <SelectValue placeholder='Tags' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Tags</SelectItem>
                    {tags.map((tag) => (
                      <SelectItem
                        key={tag._id}
                        value={tag.name}
                        className='capitalize'
                      >
                        {tag.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.sessionId}
                  onValueChange={(value) => {
                    updateFilter('sessionId', value);
                    if (value === 'All') {
                      updateFilter('groupId', 'All');
                    }
                  }}
                >
                  <SelectTrigger className='w-full md:w-[180px]'>
                    <SelectValue placeholder='Session' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Sessions</SelectItem>
                    {sessions.map((session) => (
                      <SelectItem key={session._id} value={session._id}>
                        {session.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.groupId}
                  disabled={filters.sessionId === 'All'}
                  onValueChange={(value) => {
                    updateFilter('groupId', value);
                  }}
                >
                  <SelectTrigger className='w-full md:w-[160px]'>
                    <SelectValue placeholder='Group' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='All'>All Groups</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={group._id} value={group._id}>
                        {capitalizeWords(group.fieldName)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            toolbar={(table) => (
              <>
                {hasSuperAdminAccess && (
                  <>
                    {selectedStudentsFromState.length > 0 && (
                      <Button
                        variant='destructive'
                        size='sm'
                        onClick={() => {
                          setSelectedStudents(selectedStudentsFromState);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash className='mr-2 size-4' />
                        Delete Selected ({selectedStudentsFromState.length})
                      </Button>
                    )}
                    <Button
                      variant={'outline'}
                      onClick={handleExport}
                      disabled={!hasAdminAccess}
                    >
                      <CloudUpload />
                      Export
                    </Button>
                  </>
                )}
                <EnrollioTableColumnToggle table={table} />
              </>
            )}
            columns={cols}
            data={data}
            rowCount={totalCount}
            isUpdating={isFetching}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={pagination}
            onPaginationChange={updatePagination}
            manualPagination
            manualSorting
            enableRowSelection={true}
            showSelectionCount
            getRowId={(row) => row._id}
          />
        </div>
      </CardContent>
      <DeleteStudentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedStudents={selectedStudents}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedStudents([]);
          setRowSelection({});
          selectedStudentsByIdRef.current = {};
        }}
        isBulkDelete={selectedStudents.length > 1}
        onDelete={() => {
          bulkDeleteMutation.mutate({
            studentIds: selectedStudents.map((student) => student._id),
          });
        }}
      />
    </Card>
  );
}
